import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import LogoutIcon from "@mui/icons-material/Logout";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Navbar from "../components/Navbar";
import Footer from "../components/FooterCTA";
import ContactForm from "../components/ContactForm";
import Container from "../components/Container";

/* ------------------------------------------------------------------ *
 *  CONFIG
 * ------------------------------------------------------------------ */
const ADMIN_PASSWORD = "keymechd@123"; // TODO: move real auth check to the server
const MAX_SLIDES = 8;

// Node/Express + MongoDB backend
const API_BASE = "https://keymech.org/api";
const API_URL = `${API_BASE}/projects`;
const UPLOAD_URL = `${API_BASE}/upload`;

// Image compression — kept small so uploads are fast
const MAX_IMAGE_DIMENSION = 1280;
const IMAGE_QUALITY = 0.72;

const emptyForm = { title: "", description: "", cover: "", slides: [] };

/* ------------------------------------------------------------------ *
 *  YOUR PROJECTS (shown automatically when the backend is down)
 *
 *  To show your real photos in this fallback, paste direct image links
 *  below (cover + up to 8 slides per project). Leave empty to use the
 *  purple-pink placeholder images.
 * ------------------------------------------------------------------ */
const PROJECT_IMAGES = {
  "sample-1": { cover: "", slides: [] }, // IoT Waste Monitoring System
  "sample-2": { cover: "", slides: [] }, // Hair Restoration Cap
  "sample-3": { cover: "", slides: [] }, // Pill Dispenser
  "sample-4": { cover: "", slides: [] }, // Custom STM32F407 PCB Design
};

// ---- Generated slides: tiny SVGs (a few KB each), white + one purple->pink gradient ----
const FONT = 'font-family="Arial, Helvetica, sans-serif"';
const SVG_HEAD =
  '<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="900" viewBox="0 0 1200 900">' +
  '<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">' +
  '<stop offset="0" stop-color="#EC4899"/><stop offset="1" stop-color="#7C3AED"/>' +
  "</linearGradient></defs>";

function escapeXml(t) {
  return String(t).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function wrapText(text, max) {
  const lines = [];
  let cur = "";
  for (const w of String(text).split(" ")) {
    if ((cur + " " + w).trim().length > max) {
      lines.push(cur);
      cur = w;
    } else {
      cur = (cur + " " + w).trim();
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

function svgToDataUrl(svg) {
  return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
}

function makeCoverImage(title, tagline) {
  const titleLines = wrapText(title, 20);
  const body = titleLines
    .map(
      (ln, i) =>
        `<text x="80" y="${380 + i * 88}" ${FONT} font-size="74" font-weight="700" fill="#ffffff">${escapeXml(ln)}</text>`
    )
    .join("");
  const tagY = 380 + titleLines.length * 88 + 10;
  return svgToDataUrl(
    `${SVG_HEAD}<rect width="1200" height="900" fill="url(#g)"/>
    <g fill="none" stroke="rgba(255,255,255,0.25)" stroke-width="2">
      <circle cx="960" cy="200" r="170"/><circle cx="960" cy="200" r="110"/><circle cx="960" cy="200" r="50"/>
    </g>${body}
    <text x="80" y="${tagY}" ${FONT} font-size="34" fill="rgba(255,255,255,0.88)">${escapeXml(tagline)}</text></svg>`
  );
}

function slideFrame(title, heading, inner, n, total) {
  return svgToDataUrl(
    `${SVG_HEAD}<rect width="1200" height="900" fill="#ffffff"/>
    <rect width="1200" height="14" fill="url(#g)"/>
    <text x="80" y="110" ${FONT} font-size="26" fill="#6b7280">${escapeXml(title)}</text>
    <text x="80" y="210" ${FONT} font-size="66" font-weight="700" fill="url(#g)">${escapeXml(heading)}</text>
    <rect x="80" y="236" width="120" height="8" rx="4" fill="url(#g)"/>
    ${inner}
    <text x="1120" y="860" text-anchor="end" ${FONT} font-size="24" fill="#9ca3af">${n} / ${total}</text></svg>`
  );
}

function makeContentSlide(title, heading, bullets, n, total) {
  let y = 320;
  let body = "";
  bullets.forEach((b) => {
    const lines = wrapText(b, 54);
    body += `<circle cx="100" cy="${y - 11}" r="9" fill="url(#g)"/>`;
    lines.forEach((ln, i) => {
      body += `<text x="135" y="${y + i * 42}" ${FONT} font-size="32" fill="#1f1f2e">${escapeXml(ln)}</text>`;
    });
    y += lines.length * 42 + 24;
  });
  return slideFrame(title, heading, body, n, total);
}

function makeFlowSlide(title, heading, steps, caption, n, total) {
  const count = steps.length;
  const gap = 36;
  const w = Math.floor((1040 - gap * (count - 1)) / count);
  let body = "";
  steps.forEach((step, i) => {
    const x = 80 + i * (w + gap);
    const lines = step.split("|");
    body += `<rect x="${x}" y="330" width="${w}" height="170" rx="22" fill="url(#g)"/>`;
    const startY = 415 - ((lines.length - 1) * 30) / 2 + 8;
    lines.forEach((ln, j) => {
      body += `<text x="${x + w / 2}" y="${startY + j * 30}" text-anchor="middle" ${FONT} font-size="22" font-weight="700" fill="#ffffff">${escapeXml(ln)}</text>`;
    });
    if (i < count - 1) {
      const ax = x + w + 6;
      body += `<path d="M${ax} 415 H${ax + gap - 16}" stroke="#7C3AED" stroke-width="5" stroke-linecap="round"/>`;
      body += `<path d="M${ax + gap - 18} 404 L${ax + gap - 4} 415 L${ax + gap - 18} 426 Z" fill="#7C3AED"/>`;
    }
  });
  wrapText(caption, 56).forEach((ln, i) => {
    body += `<text x="80" y="${610 + i * 44}" ${FONT} font-size="32" fill="#1f1f2e">${escapeXml(ln)}</text>`;
  });
  return slideFrame(title, heading, body, n, total);
}

function makeSampleProject(id, d) {
  const mine = PROJECT_IMAGES[id] || {};
  const T = d.title;
  const total = 7;
  const generated = [
    makeContentSlide(T, "Overview", [d.description], 1, total),
    makeContentSlide(T, "Problem & Goal", d.problem, 2, total),
    makeContentSlide(T, "Components", d.components, 3, total),
    makeFlowSlide(T, "How It Works", d.flow, d.flowCaption, 4, total),
    makeContentSlide(T, "Key Features", d.features, 5, total),
    makeContentSlide(T, "Design Details", d.design, 6, total),
    makeContentSlide(T, "Results & Next Steps", d.results, 7, total),
  ];
  return {
    id,
    _id: id,
    isSample: true,
    title: T,
    description: d.description,
    cover: mine.cover || makeCoverImage(T, d.tagline),
    slides: Array.isArray(mine.slides) && mine.slides.length ? mine.slides : generated,
  };
}

const SAMPLE_PROJECTS = [
  makeSampleProject("sample-1", {
    title: "IoT Waste Monitoring System",
    tagline: "Embedded systems + IoT + live dashboard",
    description:
      "Smart bin monitoring that measures fill level with sensors and sends live data over Wi-Fi to a dashboard, so collection is planned only when bins are actually full.",
    problem: [
      "Overflowing bins cause odour, litter and health risks",
      "Fixed collection routes waste fuel, time and money",
      "Goal: know every bin's fill level in real time",
      "Collect only when a bin actually needs it",
    ],
    components: [
      "Microcontroller with built-in Wi-Fi",
      "Ultrasonic sensor for fill-level distance",
      "Battery or regulated power supply",
      "Cloud server and web dashboard",
      "Buzzer or LED for a local full indicator",
    ],
    flow: ["Bin|Sensor", "Controller", "Wi-Fi|Network", "Cloud|Server", "Dashboard|& Alerts"],
    flowCaption:
      "The sensor reads the distance to the waste, the controller turns it into a fill percentage and sends it to the cloud, and the dashboard shows it with alerts.",
    features: [
      "Real-time fill level for every bin",
      "Alert when a bin passes a set threshold",
      "One dashboard showing the status of all bins",
      "Low-power and easy to fit on existing bins",
      "Data that helps plan efficient collection routes",
    ],
    design: [
      "Sensor mounted under the lid, facing the waste",
      "Fill % calculated from the empty-bin distance",
      "Readings sent at fixed intervals to save power",
      "Enclosure protects electronics from moisture and smell",
    ],
    results: [
      "Reliable fill-level readings during testing",
      "Shows how unnecessary collection trips can be cut",
      "Next: GPS tracking and route optimisation",
      "Next: solar power for outdoor bins",
    ],
  }),
  makeSampleProject("sample-2", {
    title: "Hair Restoration Cap",
    tagline: "Wearable electronics + light therapy",
    description:
      "Wearable low-level light therapy cap with an embedded LED array, a timed treatment cycle and rechargeable power, designed to stimulate hair follicles at home.",
    problem: [
      "Hair thinning affects confidence for many people",
      "Clinic treatments are costly and need repeat visits",
      "Goal: a safe, cordless at-home light therapy device",
      "Simple to use with automatic session control",
    ],
    components: [
      "LED array spread around the inside of the cap",
      "Constant-current driver circuits",
      "Microcontroller for timer and control logic",
      "Rechargeable battery with protection circuit",
      "Lightweight cap shell for a comfortable fit",
    ],
    flow: ["Battery|& Charger", "Protection|& Regulator", "Controller|& Timer", "LED|Drivers", "LED Array|on Scalp"],
    flowCaption:
      "Power passes through protection and regulation, the controller runs the treatment timer, and the drivers feed the LED array evenly.",
    features: [
      "Timed treatment session with automatic shut-off",
      "Even LED coverage across the scalp",
      "Rechargeable and cordless for easy use",
      "Indicators for session and battery status",
      "Lightweight and comfortable to wear",
    ],
    design: [
      "LEDs spaced for even coverage of the scalp",
      "Drivers keep current constant for steady output",
      "Heat kept low so the cap stays comfortable",
      "Protection circuit guards the battery",
    ],
    results: [
      "Working prototype completes timed sessions",
      "Light output is even across the array",
      "Next: adjustable intensity levels",
      "Next: app control and testing with specialists",
    ],
  }),
  makeSampleProject("sample-3", {
    title: "Pill Dispenser",
    tagline: "Automation + embedded control",
    description:
      "Automated medication dispenser that releases the right dose at the scheduled time and alerts the patient with a buzzer and light, reducing missed or double doses.",
    problem: [
      "Missed or doubled doses are common, especially for the elderly",
      "Managing several medicines by memory is error-prone",
      "Goal: dispense the correct dose automatically on time",
      "Remind the patient until the dose is taken",
    ],
    components: [
      "Microcontroller running the schedule logic",
      "Stepper or servo motor driving a rotating disk",
      "Real-time clock (RTC) with backup battery",
      "Buzzer and LED for reminders",
      "Display and buttons to set dose times",
    ],
    flow: ["User Sets|Schedule", "RTC|Time Check", "Controller", "Motor|Turns Disk", "Pill Out|+ Alert"],
    flowCaption:
      "At the scheduled time the controller turns the disk by one compartment, releases the dose and triggers the buzzer and light.",
    features: [
      "Multiple dose times per day",
      "Audible and visual reminders",
      "Dispenses one compartment at a time",
      "Keeps correct time even during power loss",
      "Simple operation for patients and caregivers",
    ],
    design: [
      "Rotating disk with one compartment per dose",
      "Motor steps exactly one compartment per event",
      "RTC backup battery keeps the schedule safe",
      "Enclosure blocks access to the other doses",
    ],
    results: [
      "Dispenses on schedule reliably during testing",
      "Helps reduce missed and double doses",
      "Next: sensor to confirm the pill was taken",
      "Next: caregiver notifications over Wi-Fi",
    ],
  }),
  makeSampleProject("sample-4", {
    title: "Custom STM32F407 PCB Design",
    tagline: "PCB design + ARM Cortex-M4",
    description:
      "Custom development board built around the STM32F407 ARM Cortex-M4 MCU, covering schematic capture, multi-layer PCB layout, power regulation and broken-out peripherals.",
    problem: [
      "Off-the-shelf boards limit control over layout and features",
      "Goal: design a custom board around the STM32F407",
      "Full control of power, clocks and peripherals",
      "Learn the complete flow from schematic to PCB",
    ],
    components: [
      "STM32F407 MCU, up to 168 MHz with 1 MB Flash",
      "3.3 V voltage regulator for clean power",
      "8 MHz main crystal and 32.768 kHz RTC crystal",
      "USB port and SWD/JTAG programming header",
      "Decoupling capacitors and GPIO headers",
    ],
    flow: ["Power|Input", "3.3 V|Regulator", "STM32F407|MCU", "Peripherals|USB, UART, SPI", "Debug|SWD/JTAG"],
    flowCaption:
      "Input power is regulated to 3.3 V for the MCU, which connects to the peripherals and to a debug header for programming.",
    features: [
      "Full access to GPIO through headers",
      "USB and serial interfaces on board",
      "On-board programming and debugging",
      "Clean power with proper decoupling",
      "Compact custom layout",
    ],
    design: [
      "Schematic captured in an EDA tool",
      "Ground plane for low noise and clean return paths",
      "Decoupling capacitors placed next to power pins",
      "Crystal traces kept short and away from noise",
      "Design rule checks run before fabrication",
    ],
    results: [
      "Complete flow from schematic to assembled board",
      "Board ready for firmware bring-up and testing",
      "Next: add more peripherals and sensors",
      "Next: revise layout based on test results",
    ],
  }),
];

/**
 * Compress a File -> base64 JPEG data URL (small).
 */
function fileToDataUrl(file, maxDim = MAX_IMAGE_DIMENSION, quality = IMAGE_QUALITY) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        try {
          const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
          const targetW = Math.max(1, Math.round(img.width * scale));
          const targetH = Math.max(1, Math.round(img.height * scale));
          const canvas = document.createElement("canvas");
          canvas.width = targetW;
          canvas.height = targetH;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, targetW, targetH);
          resolve(canvas.toDataURL("image/jpeg", quality));
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => reject(new Error("Could not load image"));
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Upload a compressed base64 image to the Node server.
 * Returns the public URL.
 */
async function uploadImageToServer(dataUrl) {
  const res = await fetch(UPLOAD_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ image: dataUrl }),
  });
  if (!res.ok) throw new Error("Upload failed: " + res.status);
  const data = await res.json();
  if (!data.url) throw new Error("Upload returned no URL");
  return data.url;
}

function Portfolio({ openForm }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // true when the server could not be reached and sample projects are shown
  const [usingSamples, setUsingSamples] = useState(false);

  const [isAdmin, setIsAdmin] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [loginError, setLoginError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [coverLinkInput, setCoverLinkInput] = useState("");
  const [slideLinksInput, setSlideLinksInput] = useState("");

  const [viewerItem, setViewerItem] = useState(null);
  const [slideIndex, setSlideIndex] = useState(0);

  const [showContactModal, setShowContactModal] = useState(false);

  const coverInputRef = useRef(null);
  const slidesInputRef = useRef(null);

  /* ================================================================
   *  LOAD (server-side, MongoDB via Express) with sample fallback
   * ================================================================ */
  async function loadItems() {
    try {
      setIsLoading(true);
      const res = await fetch(`${API_URL}?t=${Date.now()}`, { cache: "no-store" });
      if (!res.ok) throw new Error("HTTP " + res.status);
      const data = await res.json();
      if (!Array.isArray(data)) throw new Error("Unexpected response");
      // Mongo documents use _id — mirror it as id so the rest of the UI doesn't change
      setItems(data.map((it) => ({ ...it, id: it._id })));
      setUsingSamples(false);
    } catch (e) {
      console.error("Could not load portfolio items, using sample projects", e);
      setItems(SAMPLE_PROJECTS);
      setUsingSamples(true);
    } finally {
      setIsLoading(false);
    }
  }

  async function createProject(payload) {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Create failed: " + res.status);
    return res.json();
  }

  async function updateProject(id, payload) {
    const res = await fetch(`${API_URL}/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Update failed: " + res.status);
    return res.json();
  }

  async function deleteProjectApi(id) {
    const res = await fetch(`${API_URL}/${id}`, { method: "DELETE" });
    if (!res.ok) throw new Error("Delete failed: " + res.status);
    return res.json();
  }

  function checkAuthStatus() {
    try {
      const isAuthenticated = sessionStorage.getItem("portfolioAdmin") === "true";
      setIsAdmin(isAuthenticated);
      const path = location?.pathname || window.location.pathname;
      if (path.toLowerCase().includes("/admin") && !isAuthenticated) {
        setShowLogin(true);
      }
    } catch (e) {}
  }

  useEffect(() => {
    loadItems();
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  /* ================================================================
   *  AUTH
   * ================================================================ */
  function handleLogin(e) {
    e.preventDefault();
    if (passwordInput === ADMIN_PASSWORD) {
      setIsAdmin(true);
      setShowLogin(false);
      setPasswordInput("");
      setLoginError("");
      try {
        sessionStorage.setItem("portfolioAdmin", "true");
      } catch (e) {}
    } else {
      setLoginError("Incorrect password.");
    }
  }

  function handleLogout() {
    setIsAdmin(false);
    setShowForm(false);
    try {
      sessionStorage.removeItem("portfolioAdmin");
    } catch (e) {}
    navigate("/");
  }

  /* ================================================================
   *  FORM
   * ================================================================ */
  function handleOpenForm() {
    if (!isAdmin) {
      navigate("/admin");
      return;
    }
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
  }

  function handleOpenEditForm(item) {
    if (!isAdmin) return;
    setEditingId(item.id);
    setForm({
      title: item.title || "",
      description: item.description || "",
      cover: item.cover || "",
      slides: Array.isArray(item.slides) ? [...item.slides] : [],
    });
    setShowForm(true);
  }

  function handleCloseForm() {
    setShowForm(false);
    setForm(emptyForm);
    setEditingId(null);
    setCoverLinkInput("");
    setSlideLinksInput("");
  }

  // In demo mode (server down) images stay in the browser as data URLs.
  async function processImage(file) {
    const dataUrl = await fileToDataUrl(file);
    if (usingSamples) return dataUrl;
    return uploadImageToServer(dataUrl);
  }

  async function handleCoverUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setIsUploading(true);
      const url = await processImage(file);
      setForm((f) => ({ ...f, cover: url }));
    } catch (err) {
      console.error("Cover upload failed", err);
      alert("Couldn't upload cover image. Please try again.");
    } finally {
      setIsUploading(false);
      if (coverInputRef.current) coverInputRef.current.value = "";
    }
  }

  async function handleSlidesUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = MAX_SLIDES - form.slides.length;
    const toAdd = files.slice(0, room);
    try {
      setIsUploading(true);
      const urls = [];
      for (const f of toAdd) {
        urls.push(await processImage(f));
      }
      setForm((f) => ({ ...f, slides: [...f.slides, ...urls] }));
    } catch (err) {
      console.error("Slide upload failed", err);
      alert("Couldn't upload one or more slides. Please try again.");
    } finally {
      setIsUploading(false);
      if (slidesInputRef.current) slidesInputRef.current.value = "";
    }
  }

  function isValidImageLink(u) {
    return /^https?:\/\//i.test(u) || /^data:image\//i.test(u);
  }

  function handleAddCoverLink() {
    const link = coverLinkInput.trim();
    if (!link) return;
    if (!isValidImageLink(link)) {
      alert("Please paste a full image link starting with http:// or https://");
      return;
    }
    setForm((f) => ({ ...f, cover: link }));
    setCoverLinkInput("");
  }

  function handleAddSlideLinks() {
    // One link per line (spaces also work)
    const links = slideLinksInput.split(/\s+/).map((l) => l.trim()).filter(Boolean);
    if (!links.length) return;
    const valid = links.filter(isValidImageLink);
    const room = MAX_SLIDES - form.slides.length;
    const toAdd = valid.slice(0, room);
    if (valid.length < links.length) {
      alert("Some links were skipped because they don't start with http:// or https://");
    } else if (valid.length > room) {
      alert(`Only ${MAX_SLIDES} slides are allowed, so ${valid.length - room} link(s) were skipped.`);
    }
    if (toAdd.length) {
      setForm((f) => ({ ...f, slides: [...f.slides, ...toAdd] }));
      setSlideLinksInput("");
    }
  }

  function handleRemoveSlide(index) {
    setForm((f) => ({ ...f, slides: f.slides.filter((_, i) => i !== index) }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) {
      alert("Please add a title.");
      return;
    }

    const payload = {
      title: form.title,
      description: form.description,
      cover: form.cover,
      slides: form.slides,
    };

    // Demo mode: save locally only (lost on refresh)
    if (usingSamples) {
      if (editingId) {
        setItems((prev) =>
          prev.map((it) => (it.id === editingId ? { ...it, ...payload } : it))
        );
      } else {
        const localId = "local-" + Date.now();
        setItems((prev) => [{ ...payload, id: localId, _id: localId }, ...prev]);
      }
      handleCloseForm();
      return;
    }

    try {
      setIsSaving(true);
      if (editingId) {
        const updated = await updateProject(editingId, payload);
        setItems((prev) =>
          prev.map((it) => (it.id === editingId ? { ...updated, id: updated._id } : it))
        );
      } else {
        const created = await createProject(payload);
        setItems((prev) => [{ ...created, id: created._id }, ...prev]);
      }
      handleCloseForm();
    } catch (err) {
      console.error("Save failed", err);
      alert("Couldn't save to the server. Check your connection and try again.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this project? This can't be undone.")) return;

    // Demo mode: remove locally only
    if (usingSamples) {
      setItems((prev) => prev.filter((it) => it.id !== id));
      return;
    }

    try {
      await deleteProjectApi(id);
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch (err) {
      console.error("Delete failed", err);
      alert("Couldn't delete on the server. Check your connection and try again.");
    }
  }

  /* ================================================================
   *  VIEWER
   * ================================================================ */
  function handleOpenViewer(item) {
    if (!item.slides || item.slides.length === 0) return;
    setViewerItem(item);
    setSlideIndex(0);
  }

  function handleCloseViewer() {
    setViewerItem(null);
    setSlideIndex(0);
  }

  function handleNextSlide() {
    if (!viewerItem) return;
    setSlideIndex((i) => (i + 1) % viewerItem.slides.length);
  }

  function handlePrevSlide() {
    if (!viewerItem) return;
    setSlideIndex((i) => (i - 1 + viewerItem.slides.length) % viewerItem.slides.length);
  }

  function handleSlideImageClick(e) {
    if (!viewerItem || viewerItem.slides.length <= 1) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    if (clickX < rect.width / 2) handlePrevSlide();
    else handleNextSlide();
  }

  useEffect(() => {
    if (!viewerItem) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") handleNextSlide();
      if (e.key === "ArrowLeft") handlePrevSlide();
      if (e.key === "Escape") handleCloseViewer();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerItem, slideIndex]);

  /* ================================================================
   *  UI SECTIONS
   * ================================================================ */
  function HeroSection() {
    return (
      <section className="relative w-full bg-white overflow-hidden mb-8">
        <div className="w-full max-w-full mx-auto px-2 min-h-[60vh] flex flex-col lg:flex-row items-center gap-6 py-8 lg:py-0 lg:px-15 xl:px5">
          <div className="w-full lg:w-2/5 order-2 lg:order-1 text-center lg:text-left mt-20">
            <h1 className="font-extrabold leading-tight text-[#111]" style={{ fontSize: "clamp(28px, 4.5vw, 64px)" }}>
              Precision
              <br />
              <span className="bg-gradient-to-r from-[#EC4899] to-[#7C3AED] bg-clip-text text-transparent">
                Engineering
              </span>
            </h1>
            <p className="text-gray-600 leading-relaxed mt-4 mx-auto lg:mx-0" style={{ fontSize: "clamp(14px, 1vw, 17px)", maxWidth: "500px" }}>
              Where concepts evolve into high-performance products through
              precision design, CAD development, prototyping, and
              manufacturing expertise.
            </p>
            <div className="flex flex-wrap gap-3 mt-6 justify-center lg:justify-start">
              <Link to="/contact" className="inline-flex items-center px-6 py-3 rounded-full border-2 border-purple-300 text-purple-700 font-semibold hover:bg-purple-50 transition-all duration-300">
                Start Your Project
              </Link>
            </div>
          </div>
          <div className="w-full lg:w-3/5 order-1 lg:order-2 flex items-center justify-center lg:justify-end mt-20">
            <img
              src="https://images.unsplash.com/photo-1581092921461-eab62e97a780?auto=format&fit=crop&w=1200&q=80"
              alt="Mechanical Engineering"
              className="w-[400px] lg:w-[550px] xl:w-[650px] 2xl:w-[750px] h-auto object-contain"
              loading="lazy"
            />
          </div>
        </div>
      </section>
    );
  }

  function ProjectGridSection() {
    return (
      <section className="w-full bg-white flex-grow">
        <div className="w-full max-w-full mx-auto px-2 py-12 lg:px-10">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#111]">Projects</h2>
            {isAdmin && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleOpenForm}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#EC4899] to-[#7C3AED] text-white font-semibold shadow hover:scale-105 transition-all duration-300"
                >
                  <AddIcon fontSize="small" /> Add Project
                </button>
                <button
                  onClick={handleLogout}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition-all duration-300"
                >
                  <LogoutIcon fontSize="small" /> Log out
                </button>
              </div>
            )}
          </div>

          {/* {usingSamples && !isLoading && (
            <div className="mb-6 rounded-xl border border-purple-200 bg-purple-50 text-purple-800 text-sm px-4 py-3 flex items-center justify-between flex-wrap gap-2">
              <span>
                {isAdmin
                  ? "The server is unreachable, so sample projects are shown. Changes you make now are saved in this browser tab only and will be lost on refresh."
                  : "The server is unreachable, so sample projects are shown."}
              </span>
              <button
                onClick={loadItems}
                className="px-4 py-1.5 rounded-full border border-purple-300 bg-white text-purple-700 font-semibold hover:bg-purple-100 transition-colors"
              >
                Retry
              </button>
            </div>
          )} */}

          {isLoading ? (
            <p className="text-gray-500 text-center py-16">Loading projects…</p>
          ) : items.length === 0 ? (
            <p className="text-gray-500 text-center py-16">
              No projects yet. {isAdmin ? 'Click "Add Project" to create your first one.' : ""}
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {items.map((item) => ProjectCard({ item }))}
            </div>
          )}
        </div>
      </section>
    );
  }

  function ProjectCard({ item }) {
    return (
      <div
        key={item.id}
        onClick={() => handleOpenViewer(item)}
        className={`group relative rounded-2xl border border-gray-200 overflow-hidden bg-white shadow-sm hover:shadow-lg transition-all duration-300 ${item.slides?.length ? "cursor-pointer" : ""}`}
      >
        <div className="w-full aspect-[4/3] bg-gray-100 overflow-hidden">
          {item.cover ? (
            <img src={item.cover} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300 text-sm">No image</div>
          )}
        </div>
        <div className="p-4">
          <h3 className="font-bold text-[#111] mb-1 line-clamp-1">{item.title}</h3>
          <p className="text-gray-600 text-sm line-clamp-2">{item.description}</p>
          {item.slides?.length > 0 && (
            <p className="text-xs text-purple-600 font-semibold mt-2">
              {item.slides.length} slide{item.slides.length > 1 ? "s" : ""} &middot; click to view
            </p>
          )}
        </div>
        {isAdmin && AdminControls({ item })}
      </div>
    );
  }

  function AdminControls({ item }) {
    return (
      <div className="absolute top-3 right-3 flex gap-2">
        <button
          onClick={(e) => { e.stopPropagation(); handleOpenEditForm(item); }}
          className="w-9 h-9 rounded-full bg-white/90 shadow flex items-center justify-center text-purple-700 hover:bg-white transition-colors"
          title="Edit"
        >
          <EditIcon fontSize="small" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
          className="w-9 h-9 rounded-full bg-white/90 shadow flex items-center justify-center text-pink-600 hover:bg-white transition-colors"
          title="Delete"
        >
          <DeleteIcon fontSize="small" />
        </button>
      </div>
    );
  }

  function LoginModal() {
    if (!showLogin) return null;
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 relative">
          <button
            onClick={() => { setShowLogin(false); setPasswordInput(""); setLoginError(""); navigate("/"); }}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors"
          >
            <CloseIcon />
          </button>
          <h3 className="text-xl font-bold text-[#111] mb-4 flex items-center gap-2">
            <LockIcon className="text-purple-600" /> Admin Login
          </h3>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              autoFocus
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder="Enter password"
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 mb-2 focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
            {loginError && <p className="text-pink-600 text-sm mb-2">{loginError}</p>}
            <button type="submit" className="w-full mt-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-[#EC4899] to-[#7C3AED] text-white font-semibold shadow hover:scale-105 transition-all duration-300">
              Log in
            </button>
          </form>
        </div>
      </div>
    );
  }

  function FormModal() {
    if (!showForm) return null;
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 relative my-8">
          <button onClick={handleCloseForm} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors">
            <CloseIcon />
          </button>
          <h3 className="text-xl font-bold text-[#111] mb-4">{editingId ? "Edit Project" : "Add Project"}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Title <span className="text-pink-600">*</span></label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-purple-400"
                placeholder="Project title"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-purple-400"
                placeholder="Short description"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Cover image</label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => coverInputRef.current?.click()}
                  disabled={isUploading}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-purple-300 text-purple-700 font-semibold hover:bg-purple-50 transition-all duration-300 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <CloudUploadIcon fontSize="small" /> {isUploading ? "Processing..." : "Upload"}
                </button>
                {form.cover && (
                  <img src={form.cover} alt="cover preview" className="w-14 h-14 object-cover rounded-lg border border-gray-200" />
                )}
              </div>
              <input ref={coverInputRef} type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" />
              <div className="flex items-center gap-2 mt-3">
                <input
                  type="url"
                  value={coverLinkInput}
                  onChange={(e) => setCoverLinkInput(e.target.value)}
                  placeholder="...or paste an image link"
                  className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
                <button
                  type="button"
                  onClick={handleAddCoverLink}
                  className="px-4 py-2 rounded-full bg-gradient-to-r from-[#EC4899] to-[#7C3AED] text-white text-sm font-semibold shadow hover:scale-105 transition-all duration-300"
                >
                  Use link
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Slides ({form.slides.length}/{MAX_SLIDES})</label>
              <button
                type="button"
                disabled={form.slides.length >= MAX_SLIDES || isUploading}
                onClick={() => slidesInputRef.current?.click()}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-purple-300 text-purple-700 font-semibold hover:bg-purple-50 transition-all duration-300 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <CloudUploadIcon fontSize="small" /> {isUploading ? "Processing..." : "Upload slide(s)"}
              </button>
              <input ref={slidesInputRef} type="file" accept="image/*" multiple onChange={handleSlidesUpload} className="hidden" />
              <div className="mt-3">
                <textarea
                  value={slideLinksInput}
                  onChange={(e) => setSlideLinksInput(e.target.value)}
                  rows={3}
                  placeholder={"...or paste image links, one per line"}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
                <button
                  type="button"
                  onClick={handleAddSlideLinks}
                  disabled={form.slides.length >= MAX_SLIDES}
                  className="mt-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#EC4899] to-[#7C3AED] text-white text-sm font-semibold shadow hover:scale-105 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Add links
                </button>
              </div>
              {form.slides.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {form.slides.map((s, i) => (
                    <div key={i} className="relative">
                      <img src={s} alt={`slide ${i + 1}`} className="w-full aspect-square object-cover rounded-lg border border-gray-200" loading="lazy" />
                      <button
                        type="button"
                        onClick={() => handleRemoveSlide(i)}
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white shadow flex items-center justify-center text-pink-600 hover:bg-pink-50 transition-colors"
                      >
                        <CloseIcon style={{ fontSize: 14 }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={isUploading || isSaving}
                className="flex-1 px-6 py-2.5 rounded-full bg-gradient-to-r from-[#EC4899] to-[#7C3AED] text-white font-semibold shadow hover:scale-105 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isSaving ? "Saving..." : editingId ? "Save Changes" : "Publish Project"}
              </button>
              <button type="button" onClick={handleCloseForm} className="px-6 py-2.5 rounded-full border-2 border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition-all duration-300">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  function ContactModal() {
    if (!showContactModal) return null;
    return (
      <div
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm"
        onClick={(e) => { if (e.target === e.currentTarget) setShowContactModal(false); }}
      >
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 relative max-h-[90vh] overflow-y-auto">
          <button
            onClick={() => setShowContactModal(false)}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors z-10"
          >
            <CloseIcon />
          </button>
          <ContactForm onClose={() => setShowContactModal(false)} />
        </div>
      </div>
    );
  }

  function SlideViewer() {
    if (!viewerItem) return null;
    const hasMultiple = viewerItem.slides.length > 1;
    return (
      <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xl flex items-center justify-center p-2 sm:p-4">
        <button
          onClick={handleCloseViewer}
          className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
        >
          <CloseIcon />
        </button>
        <div className="absolute top-5 left-5 text-white text-sm font-semibold drop-shadow-md z-10">
          {viewerItem.title} &middot; {slideIndex + 1} / {viewerItem.slides.length}
        </div>
        <img
          src={viewerItem.slides[slideIndex]}
          alt={`${viewerItem.title} slide ${slideIndex + 1}`}
          onClick={hasMultiple ? handleSlideImageClick : undefined}
          className={`max-w-[96vw] max-h-[94vh] w-auto h-auto object-contain rounded-lg shadow-2xl ${hasMultiple ? "cursor-pointer" : ""}`}
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', width: '100%', maxWidth: '100vw', margin: '0 auto', boxSizing: 'border-box' }}>
      <div className="max-w-full bg-white min-h-screen flex flex-col">
        <Navbar openForm={openForm || handleOpenForm} onContactClick={() => setShowContactModal(true)} />
        <Container>
          {HeroSection()}
          {ProjectGridSection()}
          {LoginModal()}
          {FormModal()}
          {ContactModal()}
          {SlideViewer()}
        </Container>
        <Footer />
      </div>
    </div>
  );
}

export default Portfolio;